import api from './axios';

// ---- Types ----
export type TypeCompte = 'ACTIF' | 'PASSIF' | 'CHARGE' | 'PRODUIT' | 'ACTIF_PASSIF';
export type TypeJournal = 'VENTE' | 'ACHAT' | 'TRESORERIE' | 'OPERATIONS_DIVERSES' | 'A_NOUVEAU';
export type StatutEcriture = 'BROUILLON' | 'VALIDEE';
export type SourceEcriture = 'DOCUMENT' | 'PAIEMENT' | 'MANUELLE' | 'A_NOUVEAU';

export interface CompteComptable {
  id: number;
  numero: string;
  libelle: string;
  classe: number;
  type: TypeCompte;
  lettrable: boolean;
  systeme: boolean;
  actif: boolean;
}

export interface JournalComptable {
  id: number;
  code: string;
  libelle: string;
  type: TypeJournal;
  compteTresorerie?: string;
  actif: boolean;
}

export interface Mappage {
  cle: string;
  compteNumero: string;
  description?: string;
  compteLibelle?: string;
}

export interface LigneEcriture {
  id?: number;
  compteNumero: string;
  compteLibelle?: string;
  libelle?: string;
  debit: number;
  credit: number;
  tiersType?: string;
  tiersId?: number;
  lettrage?: string;
}

export interface Ecriture {
  id: number;
  numero: string;
  dateEcriture: string;
  libelle: string;
  reference?: string;
  journalCode: string;
  journalType: TypeJournal;
  sourceType: SourceEcriture;
  statut: StatutEcriture;
  totalDebit: number;
  totalCredit: number;
  equilibree: boolean;
  lignes: LigneEcriture[];
}

export interface BalanceLigne {
  compteNumero: string;
  libelle: string;
  classe: number;
  totalDebit: number;
  totalCredit: number;
  soldeDebiteur: number;
  soldeCrediteur: number;
}

export interface Balance {
  startDate: string;
  endDate: string;
  lignes: BalanceLigne[];
  totalDebit: number;
  totalCredit: number;
  totalSoldeDebiteur: number;
  totalSoldeCrediteur: number;
}

export interface GrandLivreMouvement {
  date: string;
  journalCode: string;
  ecritureNumero: string;
  libelle: string;
  debit: number;
  credit: number;
  soldeProgressif: number;
}

export interface GrandLivreCompte {
  compteNumero: string;
  libelle: string;
  mouvements: GrandLivreMouvement[];
  totalDebit: number;
  totalCredit: number;
  solde: number;
}

export interface GrandLivre {
  startDate: string;
  endDate: string;
  comptes: GrandLivreCompte[];
}

export interface GenerationResult {
  documentsComptabilises: number;
  paiementsComptabilises: number;
  ignores: number;
}

export interface RubriqueLigne {
  numero: string;
  libelle: string;
  montant: number;
}

export interface Rubrique {
  libelle: string;
  lignes: RubriqueLigne[];
  total: number;
}

export interface EtatResultat {
  startDate: string;
  endDate: string;
  produitsExploitation: Rubrique;
  chargesExploitation: Rubrique;
  resultatExploitation: number;
  produitsFinanciers: Rubrique;
  chargesFinancieres: Rubrique;
  resultatFinancier: number;
  resultatAvantImpot: number;
  impot: number;
  resultatNet: number;
}

export interface Bilan {
  startDate: string;
  endDate: string;
  actifNonCourant: Rubrique;
  actifCourant: Rubrique;
  totalActif: number;
  capitauxPropres: Rubrique;
  passifNonCourant: Rubrique;
  passifCourant: Rubrique;
  totalCapitauxPropresEtPassifs: number;
  resultatExercice: number;
  equilibre: boolean;
}

// ---- Exercices & clôture ----
export type StatutExercice = 'OUVERT' | 'CLOTURE';
export interface Exercice {
  id: number;
  libelle: string;
  dateDebut: string;
  dateFin: string;
  statut: StatutExercice;
  resultat?: number;
}
export interface ClotureResult {
  resultat: number;
  numeroRegroupement?: string;
  numeroANouveaux?: string;
}
export interface DotationResult {
  immobilisationsTraitees: number;
  totalDotation: number;
  ecritureNumero?: string;
}

// ---- Lettrage & balance âgée ----
export interface LigneTiers {
  id: number;
  date: string;
  ecritureNumero: string;
  compteNumero: string;
  libelle: string;
  debit: number;
  credit: number;
  tiersType?: string;
  tiersId?: number;
  lettrage?: string;
}
export interface SuggestionLettrage {
  montant: number;
  tiersId: number;
  tiersNom: string;
  lignes: LigneTiers[];
}
export interface BalanceAgeeLigne {
  tiersId: number;
  tiersNom: string;
  solde: number;
  nonEchu: number;
  tranche30: number;
  tranche60: number;
  tranche90: number;
  plus90: number;
}
export interface BalanceAgee {
  tiersType: string;
  dateReference: string;
  lignes: BalanceAgeeLigne[];
  totalSolde: number;
}

// ---- Immobilisations ----
export interface Immobilisation {
  id: number;
  libelle: string;
  compteImmobilisation: string;
  compteAmortissement: string;
  compteDotation: string;
  dateAcquisition: string;
  dateMiseEnService?: string;
  valeurAcquisition: number;
  valeurResiduelle: number;
  dureeMois: number;
  methode: string;
  cede: boolean;
  cumulAmortissements: number;
  valeurNetteComptable: number;
}
export interface PlanAmortissementLigne {
  annee: number;
  dotation: number;
  cumul: number;
  valeurNette: number;
}

// ---- Rapprochement bancaire ----
export type StatutRapprochement = 'EN_COURS' | 'CLOTURE';
export interface Rapprochement {
  id: number;
  compteNumero: string;
  libelle?: string;
  dateRapprochement: string;
  soldeReleve: number;
  statut: StatutRapprochement;
}
export interface LigneRapprochement {
  ligneId: number;
  date: string;
  ecritureNumero: string;
  libelle: string;
  debit: number;
  credit: number;
  pointe: boolean;
}
export interface RapprochementDetail {
  rapprochement: Rapprochement;
  lignes: LigneRapprochement[];
  soldeComptable: number;
  soldePointe: number;
  soldeNonPointe: number;
  ecart: number;
  rapproche: boolean;
}

// ---- Charges / dépenses ----
export type ReglementCharge = 'CREDIT' | 'ESPECES' | 'BANQUE';
export interface CategorieCharge {
  id: number;
  code: string;
  libelle: string;
  compteCharge: string;
  tauxTvaDefaut: number;
  systeme: boolean;
  actif: boolean;
}
export interface Charge {
  id: number;
  dateCharge: string;
  libelle: string;
  categorieCode?: string;
  compteCharge: string;
  fournisseurId?: number;
  fournisseurNom?: string;
  montantHT: number;
  tauxTva: number;
  montantTva: number;
  montantTTC: number;
  retenueSource: number;
  reglement: ReglementCharge;
  reference?: string;
  axeCode?: string;
  ecritureId?: number;
  ecritureNumero?: string;
}
export interface ChargeRequest {
  dateCharge: string;
  libelle: string;
  categorieCode?: string;
  compteCharge?: string;
  fournisseurId?: number;
  montantHT: number;
  tauxTva?: number;
  montantTva?: number;
  retenueSource?: number;
  reglement: ReglementCharge;
  reference?: string;
  /** TF-061 — axe analytique imputé sur la ligne de charge. */
  axeCode?: string;
}

// ---- TF-061 — comptabilité analytique ----
export type TypeAxe = 'PROJET' | 'DEPOT' | 'ACTIVITE' | 'AUTRE';
export interface AxeAnalytique {
  id: number; code: string; libelle: string; type: TypeAxe; actif: boolean; createdAt: string;
}
export interface AxeRapportLigne {
  axeCode: string | null; axeLibelle: string;
  charges: number; produits: number; resultat: number; nbLignes: number;
}
export interface RapportAnalytique {
  startDate: string; endDate: string; axes: AxeRapportLigne[];
  totalCharges: number; totalProduits: number; totalResultat: number;
}

// ---- API ----
export const comptabiliteService = {
  // Plan comptable
  listComptes: () => api.get<CompteComptable[]>('/comptabilite/comptes').then((r) => r.data),
  createCompte: (data: Partial<CompteComptable>) =>
    api.post<CompteComptable>('/comptabilite/comptes', data).then((r) => r.data),
  updateCompte: (id: number, data: Partial<CompteComptable>) =>
    api.put<CompteComptable>(`/comptabilite/comptes/${id}`, data).then((r) => r.data),
  deleteCompte: (id: number) => api.delete(`/comptabilite/comptes/${id}`).then((r) => r.data),
  syncPlan: () => api.post<{ ajoutes: number; version: string }>('/comptabilite/comptes/sync').then((r) => r.data),
  planVersion: () => api.get<{ version: string }>('/comptabilite/plan-version').then((r) => r.data),

  // Journaux
  listJournaux: () => api.get<JournalComptable[]>('/comptabilite/journaux').then((r) => r.data),

  // Mappages
  listMappages: () => api.get<Mappage[]>('/comptabilite/mappages').then((r) => r.data),
  updateMappage: (cle: string, compteNumero: string) =>
    api.put<Mappage>(`/comptabilite/mappages/${cle}`, { compteNumero }).then((r) => r.data),

  // Écritures
  listEcritures: (startDate?: string, endDate?: string) =>
    api.get<Ecriture[]>('/comptabilite/ecritures', {
      params: startDate && endDate ? { startDate, endDate } : undefined,
    }).then((r) => r.data),
  getEcriture: (id: number) => api.get<Ecriture>(`/comptabilite/ecritures/${id}`).then((r) => r.data),
  createEcriture: (data: {
    journalCode: string; dateEcriture: string; libelle: string; reference?: string;
    lignes: { compteNumero: string; libelle?: string; debit?: number; credit?: number; tiersType?: string; tiersId?: number }[];
  }) => api.post<Ecriture>('/comptabilite/ecritures', data).then((r) => r.data),
  validerEcriture: (id: number) =>
    api.post<Ecriture>(`/comptabilite/ecritures/${id}/valider`).then((r) => r.data),
  deleteEcriture: (id: number) => api.delete(`/comptabilite/ecritures/${id}`).then((r) => r.data),

  // Génération automatique
  generer: (startDate: string, endDate: string) =>
    api.post<GenerationResult>('/comptabilite/generer', { startDate, endDate }).then((r) => r.data),
  comptabiliserDocument: (documentId: number) =>
    api.post<Ecriture>(`/comptabilite/documents/${documentId}/comptabiliser`).then((r) => r.data),

  // États
  balance: (startDate: string, endDate: string) =>
    api.get<Balance>('/comptabilite/balance', { params: { startDate, endDate } }).then((r) => r.data),
  grandLivre: (startDate: string, endDate: string, compte?: string) =>
    api.get<GrandLivre>('/comptabilite/grand-livre', {
      params: { startDate, endDate, ...(compte ? { compte } : {}) },
    }).then((r) => r.data),
  etatResultat: (startDate: string, endDate: string) =>
    api.get<EtatResultat>('/comptabilite/etat-resultat', { params: { startDate, endDate } }).then((r) => r.data),
  bilan: (startDate: string, endDate: string) =>
    api.get<Bilan>('/comptabilite/bilan', { params: { startDate, endDate } }).then((r) => r.data),

  // Exercices & clôture
  listExercices: () => api.get<Exercice[]>('/comptabilite/exercices').then((r) => r.data),
  createExercice: (data: { libelle?: string; dateDebut: string; dateFin: string }) =>
    api.post<Exercice>('/comptabilite/exercices', data).then((r) => r.data),
  deleteExercice: (id: number) => api.delete(`/comptabilite/exercices/${id}`).then((r) => r.data),
  cloturerExercice: (id: number) =>
    api.post<ClotureResult>(`/comptabilite/exercices/${id}/cloturer`).then((r) => r.data),
  reouvrirExercice: (id: number) =>
    api.post(`/comptabilite/exercices/${id}/reouvrir`).then((r) => r.data),
  genererDotations: (id: number) =>
    api.post<DotationResult>(`/comptabilite/exercices/${id}/dotations`).then((r) => r.data),

  // Lettrage & balance âgée
  lignesTiers: (compte: string, ouvertes = true) =>
    api.get<LigneTiers[]>('/comptabilite/lettrage/lignes', { params: { compte, ouvertes } }).then((r) => r.data),
  lettrer: (ligneIds: number[]) =>
    api.post<{ code: string; nbLignes: number }>('/comptabilite/lettrage', { ligneIds }).then((r) => r.data),
  suggestionsLettrage: (compte: string) =>
    api.get<SuggestionLettrage[]>('/comptabilite/lettrage/suggestions', { params: { compte } }).then((r) => r.data),
  delettrer: (code: string) => api.delete(`/comptabilite/lettrage/${code}`).then((r) => r.data),
  balanceAgee: (type: 'CLIENT' | 'FOURNISSEUR', date?: string) =>
    api.get<BalanceAgee>('/comptabilite/balance-agee', {
      params: { type, ...(date ? { date } : {}) },
    }).then((r) => r.data),

  // Immobilisations
  listImmobilisations: () => api.get<Immobilisation[]>('/comptabilite/immobilisations').then((r) => r.data),
  createImmobilisation: (data: Partial<Immobilisation>) =>
    api.post<Immobilisation>('/comptabilite/immobilisations', data).then((r) => r.data),
  updateImmobilisation: (id: number, data: Partial<Immobilisation>) =>
    api.put<Immobilisation>(`/comptabilite/immobilisations/${id}`, data).then((r) => r.data),
  deleteImmobilisation: (id: number) => api.delete(`/comptabilite/immobilisations/${id}`).then((r) => r.data),
  planAmortissement: (id: number) =>
    api.get<PlanAmortissementLigne[]>(`/comptabilite/immobilisations/${id}/plan`).then((r) => r.data),

  // Rapprochement bancaire
  listRapprochements: () => api.get<Rapprochement[]>('/comptabilite/rapprochements').then((r) => r.data),
  createRapprochement: (data: { compteNumero: string; libelle?: string; dateRapprochement: string; soldeReleve?: number }) =>
    api.post<Rapprochement>('/comptabilite/rapprochements', data).then((r) => r.data),
  rapprochementDetail: (id: number) =>
    api.get<RapprochementDetail>(`/comptabilite/rapprochements/${id}`).then((r) => r.data),
  pointer: (id: number, ligneId: number, pointe: boolean) =>
    api.post<RapprochementDetail>(`/comptabilite/rapprochements/${id}/pointage`, { ligneId, pointe }).then((r) => r.data),
  cloturerRapprochement: (id: number) =>
    api.post<Rapprochement>(`/comptabilite/rapprochements/${id}/cloturer`).then((r) => r.data),
  deleteRapprochement: (id: number) => api.delete(`/comptabilite/rapprochements/${id}`).then((r) => r.data),

  // Charges / dépenses
  listCategoriesCharge: () => api.get<CategorieCharge[]>('/comptabilite/categories-charge').then((r) => r.data),
  listCharges: () => api.get<Charge[]>('/comptabilite/charges').then((r) => r.data),
  saisirCharge: (data: ChargeRequest) =>
    api.post<Charge>('/comptabilite/charges', data).then((r) => r.data),
  deleteCharge: (id: number) => api.delete(`/comptabilite/charges/${id}`).then((r) => r.data),
  // TF-067 — pré-imputation IA d'une charge depuis son libellé
  suggererImputation: (libelle: string) =>
    api.post<ImputationSuggestion>('/assistant/imputation', { libelle }).then((r) => r.data),

  // TF-061 — comptabilité analytique (axes + rapport)
  listAxes: () => api.get<AxeAnalytique[]>('/comptabilite/axes').then((r) => r.data),
  createAxe: (data: { code: string; libelle: string; type?: TypeAxe }) =>
    api.post<AxeAnalytique>('/comptabilite/axes', data).then((r) => r.data),
  updateAxe: (id: number, data: Partial<{ libelle: string; type: TypeAxe; actif: boolean }>) =>
    api.put<AxeAnalytique>(`/comptabilite/axes/${id}`, data).then((r) => r.data),
  deleteAxe: (id: number) => api.delete(`/comptabilite/axes/${id}`).then((r) => r.data),
  rapportAnalytique: (startDate: string, endDate: string) =>
    api.get<RapportAnalytique>('/comptabilite/analytique', { params: { startDate, endDate } }).then((r) => r.data),

  // TF-062 — espace expert-comptable (lecture + commentaires + invitation)
  expertEcritures: (startDate?: string, endDate?: string) =>
    api.get<Ecriture[]>('/expert-comptable/ecritures', { params: { startDate, endDate } }).then((r) => r.data),
  commentairesEcriture: (ecritureId: number) =>
    api.get<CommentaireEcriture[]>(`/expert-comptable/ecritures/${ecritureId}/commentaires`).then((r) => r.data),
  commenterEcriture: (ecritureId: number, texte: string) =>
    api.post<CommentaireEcriture>(`/expert-comptable/ecritures/${ecritureId}/commentaires`, { texte }).then((r) => r.data),
  supprimerCommentaire: (id: number) =>
    api.delete(`/expert-comptable/commentaires/${id}`).then((r) => r.data),
  inviterExpert: (data: { email: string; nom: string; prenom?: string }) =>
    api.post<{ utilisateurId: number; email: string; dejaExistant: boolean }>('/expert-comptable/inviter', data)
      .then((r) => r.data),
};

// TF-062 — commentaire d'écriture
export interface CommentaireEcriture {
  id: number; ecritureId: number; auteur: string; role: string | null;
  texte: string; createdAt: string;
}

/** Suggestion d'imputation comptable (TF-067). */
export interface ImputationSuggestion {
  categorieCode: string | null;
  categorieLibelle: string | null;
  compteCharge: string | null;
  tauxTvaDefaut: number | null;
  commentaire: string;
  genereParIa: boolean;
}

export default comptabiliteService;
